// "use client";

import React from "react";
import { Card, Typography, Form as AntForm, Input, Button, Space } from "antd";
import { MailOutlined, LockOutlined } from "@ant-design/icons";
import { Formik } from "formik";

const { Title, Text } = Typography;

export type LoginCardProps = {
    onSubmit: (values: { email: string; password: string }) => Promise<void> | void;
    loading?: boolean;

    /** Логотип: передай <BrandLogo ... /> */
    logo?: React.ReactNode;
    /** Заголовок, по умолчанию "Welcome Back" */
    title?: React.ReactNode;

    /** Значения по умолчанию */
    initialEmail?: string;

    /** Ссылки/хэндлеры */
    signUpHref?: string;
    onSignUpClick?: () => void;
    forgotHref?: string;
    onForgotClick?: () => void;

    /** Доп. классы/стили внешнего контейнера (Card) */
    className?: string;
    style?: React.CSSProperties;
};

export function LoginCard({
    onSubmit,
    loading,
    logo,
    title = <>С возвращением</>,
    initialEmail = "",
    signUpHref,
    onSignUpClick,
    forgotHref,
    onForgotClick,
    className,
    style,
}: LoginCardProps) {
    // простая валидация без yup
    const validate = (values: { email: string; password: string }) => {
        const errors: Partial<typeof values> = {};
        if (!values.email) errors.email = "Введите e-mail";
        else if (!/^\S+@\S+\.\S+$/.test(values.email)) errors.email = "Некорректный e-mail";

        if (!values.password) errors.password = "Введите пароль";
        else if (values.password.length < 6) errors.password = "Минимум 6 символов";
        return errors;
    };

    const showForgotAction = Boolean(forgotHref || onForgotClick);
    const showSignUpAction = Boolean(signUpHref || onSignUpClick);
    const shouldRenderActions = showForgotAction || showSignUpAction;

    return (
        <Card
            className={className}
            style={{
                maxWidth: 460,
                margin: "0 auto",
                boxShadow: "0 8px 24px rgba(0,0,0,0.06)",
                ...style,
            }}
            styles={{ body: { padding: 32 } }}
        >
            <Space direction="vertical" align="center" style={{ width: "100%" }} size={24}>
                {logo}
                <Title level={2} style={{ margin: 0, textAlign: "center", lineHeight: 1.1 }}>
                    {title}
                </Title>

                <Formik
                    initialValues={{ email: initialEmail, password: "" }}
                    validate={validate}
                    onSubmit={async (values, { setSubmitting }) => {
                        try {
                            await onSubmit(values);
                        } finally {
                            setSubmitting(false);
                        }
                    }}
                >
                    {({
                        handleSubmit,
                        handleChange,
                        handleBlur,
                        values,
                        errors,
                        touched,
                        isSubmitting,
                    }) => (
                        <AntForm
                            layout="vertical"
                            onFinish={handleSubmit}
                            style={{ width: "100%", marginTop: 8 }}
                        >
                            <AntForm.Item
                                label="Электронная почта"
                                validateStatus={touched.email && errors.email ? "error" : undefined}
                                help={touched.email && errors.email ? errors.email : undefined}
                            >
                                <Input
                                    name="email"
                                    type="email"
                                    placeholder="e-mail"
                                    prefix={<MailOutlined />}
                                    value={values.email}
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    allowClear
                                    style={{ borderRadius: 9999, height: 44 }}
                                />
                            </AntForm.Item>

                            <AntForm.Item
                                label="Пароль"
                                validateStatus={
                                    touched.password && errors.password ? "error" : undefined
                                }
                                help={
                                    touched.password && errors.password
                                        ? errors.password
                                        : undefined
                                }
                            >
                                <Input.Password
                                    name="password"
                                    placeholder="пароль"
                                    prefix={<LockOutlined />}
                                    value={values.password}
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    onPressEnter={(e) => {
                                        e.currentTarget.blur(); // чтобы Formik зафиксировал touched
                                        handleSubmit();
                                    }}
                                    style={{ borderRadius: 9999, height: 44 }}
                                />
                            </AntForm.Item>

                            <Button
                                htmlType="submit"
                                type="primary"
                                size="large"
                                block
                                loading={loading || isSubmitting}
                                style={{ borderRadius: 9999, height: 44 }}
                            >
                                Вход в аккаунт
                            </Button>

                            {shouldRenderActions && (
                                <Space
                                    direction="vertical"
                                    align="center"
                                    style={{ width: "100%", marginTop: 16 }}
                                    size={6}
                                >
                                    {showForgotAction && (
                                        <Text type="secondary">
                                            <a
                                                href={forgotHref}
                                                onClick={(e) => {
                                                    if (onForgotClick) {
                                                        e.preventDefault();
                                                        onForgotClick();
                                                    }
                                                }}
                                            >
                                                Забыли пароль?
                                            </a>
                                        </Text>
                                    )}

                                    {showSignUpAction && (
                                        <Text type="secondary">
                                            У вас нет учетной записи?{" "}
                                            <a
                                                href={signUpHref}
                                                onClick={(e) => {
                                                    if (onSignUpClick) {
                                                        e.preventDefault();
                                                        onSignUpClick();
                                                    }
                                                }}
                                            >
                                                Зарегистрироваться
                                            </a>
                                        </Text>
                                    )}
                                </Space>
                            )}
                        </AntForm>
                    )}
                </Formik>
            </Space>
        </Card>
    );
}
